/**
 * Patches global fetch to inject Authorization header for /bff requests.
 * Uses the Amplify session token from the current authenticated user.
 */

import { getCognitoTokenAsync } from './get-cognito-token.js';

const originalFetch = window.fetch.bind(window);

function isBffRequest(url: string): boolean {
  if (url.startsWith('/bff') || url.startsWith('/bff/')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.startsWith('/bff');
  } catch {
    return false;
  }
}

function withAuthHeaders(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string,
): [RequestInfo | URL, RequestInit | undefined] {
  const headers = new Headers(input instanceof Request ? input.headers : init?.headers);
  headers.set('Authorization', `Bearer ${token}`);

  if (input instanceof Request) {
    return [new Request(input, { headers }), init];
  }

  return [input, { ...init, headers }];
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

  if (isBffRequest(url)) {
    let token = await getCognitoTokenAsync();

    if (!token) {
      token = await getCognitoTokenAsync({ forceRefresh: true });
    }

    if (token) {
      const [nextInput, nextInit] = withAuthHeaders(input, init, token);
      return originalFetch(nextInput, nextInit);
    }

    console.warn('[auth-fetch] No token found for:', url);
  }

  return originalFetch(input, init);
};
